/**
 * MSW Override: Booking Confirm Fail-Once scenario
 *
 * The FIRST payment attempt for a given booking fails with a definitive
 * payment failure (code CORE-SUBSCRIPTION-BOOKING-PAYMENT-FAILED). The frontend
 * treats this as final: it skips the recovery poll, surfaces the error, renews
 * the Spreedly token and clears the card fields (CVO-19150/19151). Every
 * subsequent attempt for that same booking is passed through to the real
 * handler, so a retry can be used to verify the frontend re-tokenizes and
 * succeeds.
 *
 * Enable by setting ENABLE_MSW=true and
 * NUXT_PUBLIC_MSW_OVERRIDES_BOOKING_CONFIRM_FAIL_ONCE=true in your .env,
 * then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse, bypass } from 'msw';
import { BFF_ENDPOINTS, toMSWPattern } from '@cvo/domain';

const failedOnceForBookingIds = new Set<string>();

export function createBookingConfirmFailOnceOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  return [http.post(
    `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.BOOKING_PAYMENTS)}`,
    async ({ request, params }) => {
      const bookingId = params.bookingId as string;

      if (!failedOnceForBookingIds.has(bookingId)) {
        failedOnceForBookingIds.add(bookingId);
        console.log('[MSW] booking confirm fail-once: failing first attempt (payment failed) for booking', bookingId);

        // Must match BOOKING_PAYMENT_FAILED_ERROR_CODE in app/constants/booking.ts;
        // this code makes the frontend skip the recovery poll (isBookingPaymentFailedError).
        return HttpResponse.json(
          { code: 'CORE-SUBSCRIPTION-BOOKING-PAYMENT-FAILED', message: 'The booking payment failed.' },
          { status: 400 },
        );
      }

      console.log('[MSW] booking confirm fail-once: passing retry through for booking', bookingId);

      const originalResponse = await fetch(bypass(request));
      return HttpResponse.json(await originalResponse.json(), { status: originalResponse.status });
    },
  )];
}
